import { useEffect, useId, useState } from 'react'
import { Badge } from '../catalyst/badge'
import { Input } from '../catalyst/input'

// A name box that suggests things to link the entry to: a registered product, an inventory item, a
// name used before. Typing filters the suggestions; picking one links the entry; "Use what I typed"
// keeps the text as it is. The list sits under the box (not a floating popup), short enough to fit
// above a phone's keyboard, and looks the same in every browser. Wrap it in a Catalyst `Field` with
// a `Label`, like any other input.

export type PickerOption = {
  id: string
  label: string
  /** A second line: what it is (ingredients and PCP, type and make ...). */
  detail?: string
  badge?: string
}

/** Options under an optional heading ("Inventory", "Used before"). Empty groups are left out. */
export type PickerGroup = { label?: string; options: PickerOption[] }

export function PickerInput({
  value,
  onChange,
  onPick,
  groups,
  linked,
  hint,
  minChars = 0,
  busy = false,
  emptyText,
  footerAction,
  listLabel,
  ...inputProps
}: {
  value: string
  /** The text as typed. Typing after a pick should unlink it: the name no longer matches. */
  onChange: (text: string) => void
  onPick: (option: PickerOption) => void
  /** What to suggest for the current text, already filtered. */
  groups: PickerGroup[]
  /** Shown under the box once the entry is linked (the list stays closed while it is). */
  linked: React.ReactNode | null
  /** Shown under the box while it is not linked and the list is closed. */
  hint?: React.ReactNode
  /** Characters to type before the list opens: 0 shows everything on focus. */
  minChars?: number
  /** A search is under way: a spinner in the box, "Searching…" in an empty list. */
  busy?: boolean
  emptyText: string
  /** One extra action in the list's footer, e.g. "Search all products". */
  footerAction?: { label: string; onClick: () => void }
  /** Names the list for screen readers, e.g. "Matching products". */
  listLabel: string
} & Omit<React.ComponentPropsWithoutRef<typeof Input>, 'value' | 'onChange'>) {
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(-1)
  const listId = useId()
  const shown = groups.filter((g) => g.options.length > 0)
  const options = shown.flatMap((g) => g.options)
  const showList = open && !linked && value.trim().length >= minChars
  const optionId = (i: number) => `${listId}-${i}`

  // New text means new suggestions: start with nothing highlighted.
  useEffect(() => setActive(-1), [value])

  // Keep the highlighted row in view when it is moved with the keys.
  useEffect(() => {
    if (active >= 0) document.getElementById(optionId(active))?.scrollIntoView({ block: 'nearest' })
  }, [active])

  function choose(option: PickerOption) {
    setOpen(false)
    setActive(-1)
    onPick(option)
  }

  // The combobox keys: arrows move the highlight, Enter picks it, Esc closes the list.
  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      if (showList) {
        e.preventDefault()
        e.stopPropagation()
        setOpen(false)
        setActive(-1)
      }
      return
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (linked) return
      e.preventDefault()
      if (!open) {
        setOpen(true)
        return
      }
      if (options.length === 0) return
      const last = options.length - 1
      setActive((i) => (e.key === 'ArrowDown' ? (i >= last ? 0 : i + 1) : i <= 0 ? last : i - 1))
      return
    }
    if (e.key === 'Enter' && showList && active >= 0 && options[active]) {
      e.preventDefault()
      choose(options[active])
    }
  }

  let index = -1
  return (
    <div data-slot="control">
      <span className="relative block">
        <Input
          {...inputProps}
          value={value}
          autoComplete="off"
          role="combobox"
          aria-busy={busy}
          aria-expanded={showList && options.length > 0}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={showList && active >= 0 ? optionId(active) : undefined}
          onKeyDown={onKeyDown}
          onFocus={(e) => {
            setOpen(true)
            inputProps.onFocus?.(e)
          }}
          // Leaving the box closes the list. Taps inside the list don't take focus (see onMouseDown below).
          onBlur={() => setOpen(false)}
          onChange={(e) => {
            setOpen(true)
            onChange(e.target.value)
          }}
        />
        {busy && showList && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 animate-spin rounded-full border-2 border-zinc-300 border-t-emerald-600 dark:border-white/20 dark:border-t-emerald-400"
          />
        )}
      </span>
      <span role="status" className="sr-only">
        {busy && showList ? 'Searching' : ''}
      </span>

      {linked ? (
        <p className="mt-2 text-sm/6 text-emerald-700 dark:text-emerald-400">✓ {linked}</p>
      ) : !showList && hint ? (
        <p className="mt-2 text-sm/6 text-zinc-500 dark:text-zinc-400">{hint}</p>
      ) : null}

      {showList && (
        // Keep focus in the box while an option or footer button is pressed, so its click lands
        // before the box's blur closes the list.
        <div className="mt-2 rounded-lg ring-1 ring-zinc-950/10 dark:ring-white/10" onMouseDown={(e) => e.preventDefault()}>
          {options.length > 0 ? (
            // Short and scrollable, so it fits above a phone's keyboard.
            <div id={listId} role="listbox" aria-label={listLabel} className="max-h-52 overflow-y-auto overscroll-contain">
              {shown.map((group, g) => (
                <div key={group.label ?? g} role="group" aria-label={group.label}>
                  {group.label && (
                    <div className="sticky top-0 bg-zinc-50 px-3 py-1 text-xs/5 font-medium text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400">{group.label}</div>
                  )}
                  <ul className="divide-y divide-zinc-950/5 dark:divide-white/10">
                    {group.options.map((o) => {
                      const i = ++index
                      return (
                        <li key={o.id} id={optionId(i)} role="option" aria-selected={i === active}>
                          <button
                            type="button"
                            tabIndex={-1}
                            className={`block w-full px-3 py-1.5 text-left hover:bg-zinc-50 dark:hover:bg-white/5 ${i === active ? 'bg-zinc-100 dark:bg-white/10' : ''}`}
                            onClick={() => choose(o)}
                          >
                            <span className="flex items-center gap-2">
                              <span className="truncate text-sm/5 font-medium text-zinc-950 dark:text-white">{o.label}</span>
                              {o.badge && <Badge color="zinc">{o.badge}</Badge>}
                            </span>
                            {o.detail && <span className="block truncate text-xs/5 text-zinc-500 dark:text-zinc-400">{o.detail}</span>}
                          </button>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              ))}
            </div>
          ) : (
            <p className="px-3 py-2 text-sm/6 text-zinc-500 dark:text-zinc-400">{busy ? 'Searching…' : emptyText}</p>
          )}
          {(footerAction || value.trim()) && (
            <div className="flex items-center justify-between gap-3 border-t border-zinc-950/5 px-3 py-1 text-sm/6 dark:border-white/10">
              {footerAction ? (
                <button type="button" className="min-h-9 font-medium text-emerald-700 dark:text-emerald-400" onClick={footerAction.onClick}>
                  {footerAction.label}
                </button>
              ) : (
                <span />
              )}
              {value.trim() && (
                <button type="button" className="min-h-9 text-zinc-500 dark:text-zinc-400" onClick={() => setOpen(false)}>
                  Use what I typed
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/** The options whose name or detail contains the text, case-insensitively; all of them for empty text. */
export function filterOptions(options: PickerOption[], text: string): PickerOption[] {
  const q = text.trim().toLowerCase()
  if (!q) return options
  return options.filter((o) => o.label.toLowerCase().includes(q) || o.detail?.toLowerCase().includes(q))
}
